import { hasValue } from "@arti/utilities";
import { type FC, type ReactNode, useState } from "react";

import type { RemoteIssueProject } from "../../api/issueIntegrations";
import type {
  IssueCredential,
  IssueProject,
  IssueProviderDetail,
} from "../../types";

import { createIssueIntegrationProjectAsUser } from "../../dashboard-client";
import { useFormAction } from "../../hooks/useFormAction";
import { useRemoteProjects } from "../issues/useRemoteProjects";
import { JiraLinkCodeBlock } from "../jira/JiraLinkCodeBlock";
import { Alert } from "./Alert";
import { CopyButton } from "./CopyButton";
import { SearchableOptionList } from "./SearchableOptionList";
import { Text } from "./Text";

// ---------------------------------------------------------------------------
// Step style helpers
// ---------------------------------------------------------------------------

function stepBorderClass(active: boolean, done: boolean): string {
  if (active) {
    return "border-emerald-300 bg-emerald-50/40 dark:border-emerald-500/30 dark:bg-emerald-500/10";
  }
  if (done) {
    return "border-emerald-300/80 bg-emerald-50/20 dark:border-emerald-500/20 dark:bg-emerald-500/5";
  }
  return "border-slate-300 bg-white/60 dark:border-slate-700 dark:bg-slate-900/40";
}

function stepBadgeClass(active: boolean, done: boolean): string {
  if (done) {
    return "border-emerald-300 bg-emerald-100 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/20 dark:text-emerald-300";
  }
  if (active) {
    return "border-emerald-300 bg-slate-900 text-emerald-300 dark:border-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-300";
  }
  return "border-slate-300 bg-slate-100 text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400";
}

function stepTitleClass(active: boolean, done: boolean): string {
  if (done) {
    return "text-emerald-700 dark:text-emerald-300";
  }
  if (active) {
    return "text-slate-900 dark:text-white";
  }
  return "text-slate-500 dark:text-slate-400";
}

// ---------------------------------------------------------------------------
// SetupSteps
// ---------------------------------------------------------------------------

export const SetupSteps: FC<{ currentStep: number }> = ({ currentStep }) => {
  const steps = [
    {
      detail:
        "Open the installation link below and complete Atlassian's install flow.",
      title: "Install the Arti app in Jira",
    },
    {
      detail:
        "Once the app is installed, come back here and generate a link code.",
      title: "Generate a link code",
    },
    {
      detail:
        "Open the Arti Integration Settings page in Jira admin and paste the link code.",
      title: "Link in Jira",
    },
  ];

  return (
    <div className="space-y-2">
      {steps.map((step, index) => {
        const done = index < currentStep;
        const active = index === currentStep;
        return (
          <div
            key={step.title}
            className={`flex items-start gap-3 rounded-none border px-3 py-2.5 transition-colors ${stepBorderClass(active, done)}`}
          >
            <span
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-none border font-mono text-[10px] font-bold ${stepBadgeClass(active, done)}`}
            >
              {done ? "✓" : index + 1}
            </span>
            <div>
              <p
                className={`font-mono text-[11px] font-bold uppercase tracking-widest ${stepTitleClass(active, done)}`}
              >
                {step.title}
              </p>
              <p
                className={`text-xs ${
                  active
                    ? "text-slate-600 dark:text-slate-300"
                    : "text-slate-500 dark:text-slate-400"
                }`}
              >
                {step.detail}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ChecklistItem
// ---------------------------------------------------------------------------

export const ChecklistItem: FC<{
  label: string;
  detail: string;
  ok: boolean;
  expandable?: boolean;
  children?: ReactNode;
}> = ({ label, detail, ok, expandable, children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="rounded-none border border-slate-300 dark:border-slate-700">
      <div
        className={`flex items-center gap-2 px-3 py-2 ${expandable ? "cursor-pointer select-none" : ""}`}
        onClick={expandable ? () => setIsExpanded((v) => !v) : undefined}
      >
        <span
          className={`flex h-5 w-5 items-center justify-center rounded-none border text-xs ${ok ? "border-green-200 bg-green-100 text-green-600 dark:border-green-500/20 dark:bg-green-500/20 dark:text-green-400" : "border-amber-200 bg-amber-100 text-amber-600 dark:border-amber-500/20 dark:bg-amber-500/20 dark:text-amber-400"}`}
        >
          {ok ? "✓" : "!"}
        </span>
        <Text variant="label" className="text-gray-900 dark:text-white">
          {label}
        </Text>
        <Text className="ml-auto">{detail}</Text>
        {expandable && (
          <svg
            className={`h-4 w-4 text-gray-400 transition-transform dark:text-white/40 ${isExpanded ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        )}
      </div>
      {expandable && isExpanded && children && (
        <div className="border-t border-gray-200 px-3 py-2 dark:border-white/10">
          {children}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Install blocks (ConnectUrlBlock / ForgeInstallCommandBlock)
// ---------------------------------------------------------------------------

const LinkIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
    />
  </svg>
);

const TerminalIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

function deriveInstallLinkLabel(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

export const ConnectUrlBlock: FC<{ connectUrl: string }> = ({ connectUrl }) => (
  <div className="overflow-hidden rounded-none border border-slate-300 bg-gradient-to-br from-slate-50 via-white to-emerald-50/50 dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-emerald-500/5">
    <div className="px-4 py-3">
      <div className="flex items-center gap-2.5">
        <div className="flex h-7 w-7 items-center justify-center rounded-none border border-emerald-300 bg-slate-900 dark:border-emerald-500/30 dark:bg-slate-950">
          <LinkIcon className="h-3.5 w-3.5 text-emerald-300 dark:text-emerald-300" />
        </div>
        <div>
          <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-slate-900 dark:text-white">
            Install the Arti app
          </p>
          <Text as="p">{deriveInstallLinkLabel(connectUrl)}</Text>
        </div>
      </div>
      <Text as="p" className="mt-2 leading-relaxed">
        Open this link and follow the Atlassian installation flow to install the
        app on your Jira site.
      </Text>
    </div>
    <div className="border-t border-slate-300 bg-slate-100/70 px-4 py-2.5 dark:border-slate-700 dark:bg-slate-900/70">
      <a
        href={connectUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex w-full items-center justify-center gap-1.5 rounded-none border border-emerald-500 bg-emerald-600 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-white transition-colors hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 dark:bg-emerald-500 dark:text-slate-950 dark:hover:bg-emerald-400"
      >
        Open installation link
        <svg
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
          />
        </svg>
      </a>
    </div>
  </div>
);

const FORGE_INSTALL_COMMAND = "cd packages/forge-jira-ui && forge install";

export const ForgeInstallCommandBlock: FC = () => (
  <div className="overflow-hidden rounded-none border border-slate-300 bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950">
    <div className="px-4 py-3">
      <div className="flex items-center gap-2.5">
        <div className="flex h-7 w-7 items-center justify-center rounded-none border border-slate-300 bg-slate-900 dark:border-slate-700 dark:bg-slate-950">
          <TerminalIcon className="h-3.5 w-3.5 text-emerald-300 dark:text-emerald-300" />
        </div>
        <p className="font-mono text-[11px] font-bold uppercase tracking-widest text-slate-900 dark:text-white">
          Install via CLI
        </p>
      </div>
      <Text as="p" className="mt-2 leading-relaxed">
        No installation link is configured. Run this command from the project
        root to install the Forge app:
      </Text>
    </div>
    <div className="flex items-center gap-2 border-t border-slate-300 bg-slate-100/70 px-4 py-2.5 dark:border-slate-700 dark:bg-slate-900/70">
      <code className="flex-1 break-all font-mono text-xs text-slate-700 dark:text-slate-300">
        {FORGE_INSTALL_COMMAND}
      </code>
      <CopyButton text={FORGE_INSTALL_COMMAND} />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// ProjectAdder (inline project add for expandable checklist item)
// ---------------------------------------------------------------------------

const ProjectAdder: FC<{
  providerId: string;
  credentials: IssueCredential[];
  existingProjects: IssueProject[];
  onRefresh: () => void;
}> = ({ providerId, credentials, existingProjects, onRefresh }) => {
  const remote = useRemoteProjects(providerId, credentials);
  const { saving, error, message, run } = useFormAction();

  const handleSelect = (remoteId: string) => {
    if (!remoteId || saving) {
      return;
    }
    const project = remote.projects.find((p) => p.remote_id === remoteId);
    if (!project) {
      return;
    }

    void run(async () => {
      await createIssueIntegrationProjectAsUser({
        body: {
          external_key: project.external_key,
          name: project.name ?? undefined,
          project_id: project.suggested_project_id || project.remote_id,
        },
        path: { providerId },
      });
      onRefresh();
      return "Project added.";
    });
  };

  // Already-connected projects list
  const connectedList =
    existingProjects.length > 0 ? (
      <div className="space-y-1">
        <Text variant="label">Connected</Text>
        <ul className="space-y-1">
          {existingProjects.map((p) => (
            <li
              key={p.project_id}
              className="flex items-center gap-2 rounded-none border border-slate-300 bg-slate-50/70 px-2.5 py-1.5 dark:border-slate-700 dark:bg-slate-900/50"
            >
              <span className="flex h-4 w-4 items-center justify-center rounded-none border border-emerald-300 bg-emerald-100 font-mono text-[10px] text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/20 dark:text-emerald-300">
                ✓
              </span>
              <span className="truncate text-xs text-slate-700 dark:text-slate-300">
                {p.name ?? p.external_key}
              </span>
              <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">
                {p.external_key}
              </span>
            </li>
          ))}
        </ul>
      </div>
    ) : null;

  if (remote.status === "loading") {
    return (
      <div className="space-y-3">
        {connectedList}
        <p className="text-xs text-gray-500 dark:text-white/40">
          Loading projects...
        </p>
      </div>
    );
  }

  if (remote.status === "error") {
    return (
      <div className="space-y-3">
        {connectedList}
        <Alert variant="error">
          {remote.error ?? "Failed to load remote projects."}
        </Alert>
      </div>
    );
  }

  if (!remote.hasCredentials) {
    return (
      <div className="space-y-3">
        {connectedList}
        <p className="text-xs text-gray-500 dark:text-white/40">
          Authorize credentials first to browse Jira projects.
        </p>
      </div>
    );
  }

  // Filter out already-connected projects from the dropdown
  const existingKeys = new Set(existingProjects.map((p) => p.external_key));
  const availableRemote = remote.projects.filter(
    (p: RemoteIssueProject) => !existingKeys.has(p.external_key),
  );

  return (
    <div className="space-y-3">
      {connectedList}
      {availableRemote.length > 0 ? (
        <div className="space-y-2">
          <SearchableOptionList
            label="Add Jira project"
            selectedValue={null}
            onSelect={handleSelect}
            disabled={saving}
            searchPlaceholder="Search Jira projects..."
            emptyMessage="No Jira projects match your search."
            items={availableRemote.map((project: RemoteIssueProject) => ({
              description:
                project.name && project.name !== project.external_key
                  ? project.external_key
                  : project.suggested_project_id || null,
              keywords: [
                project.name ?? "",
                project.external_key,
                project.suggested_project_id || "",
              ],
              label: project.name ?? project.external_key,
              meta: project.suggested_project_id || null,
              value: project.remote_id,
            }))}
            maxHeightClassName="max-h-80"
          />
          <Text className="leading-relaxed text-slate-500 dark:text-slate-400">
            Search by project name, external key, or suggested project id.
          </Text>
        </div>
      ) : (
        <p className="text-xs text-gray-500 dark:text-white/40">
          All available projects are already connected.
        </p>
      )}
      {saving && (
        <p className="text-xs text-gray-500 dark:text-white/40">Adding...</p>
      )}
      {error && <Alert variant="error">{error}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// InstalledView
// ---------------------------------------------------------------------------

interface InstalledViewProps {
  detail: IssueProviderDetail;
  footer?: ReactNode;
  onRefresh: () => void;
}

export const InstalledView: FC<InstalledViewProps> = ({
  detail,
  footer,
  onRefresh,
}) => {
  const { provider, credentials, projects, webhook } = detail;

  const isConnectProvider =
    hasValue(provider.connect.client_key) ||
    hasValue(provider.connect.app_key) ||
    credentials.some(
      (c) =>
        c.kind === "connect_shared_secret" ||
        c.credential_id.endsWith("-shared-secret"),
    );

  return (
    <div className="space-y-4">
      {/* Status card */}
      <div className="rounded-none border border-slate-300 bg-gradient-to-br from-slate-50/70 via-white to-emerald-50/30 p-4 dark:border-slate-700 dark:from-slate-900/80 dark:via-slate-900 dark:to-emerald-500/5">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-none border border-emerald-300 bg-slate-900 dark:border-emerald-500/30 dark:bg-slate-950">
            <svg
              className="h-4 w-4 text-emerald-300 dark:text-emerald-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
          </div>
          <div>
            <h3 className="font-mono text-[11px] font-bold uppercase tracking-widest text-slate-900 dark:text-white">
              {provider.display_name}
            </h3>
            <Text
              variant="label"
              className={
                provider.is_enabled
                  ? "text-emerald-600 dark:text-emerald-300"
                  : "text-slate-500 dark:text-slate-400"
              }
            >
              {provider.is_enabled ? "Enabled" : "Disabled"}
            </Text>
          </div>
        </div>
      </div>

      {/* Setup checklist */}
      <div className="space-y-2">
        <Text variant="label" as="h4">
          Setup Status
        </Text>

        <ChecklistItem
          label="App installed"
          detail={isConnectProvider ? "Via Connect" : "Manual"}
          ok
        />
        <ChecklistItem
          label="Credentials"
          detail={`${credentials.length} configured`}
          ok={credentials.length > 0}
        />
        <ChecklistItem
          label="Projects"
          detail={`${projects.length} connected`}
          ok={projects.length > 0}
          expandable
        >
          <ProjectAdder
            providerId={provider.provider_id}
            credentials={credentials}
            existingProjects={projects}
            onRefresh={onRefresh}
          />
        </ChecklistItem>
        <ChecklistItem
          label="Webhook"
          detail={webhook ? "Configured" : "Not configured"}
          ok={webhook !== null}
        />
      </div>

      {/* Link code for Jira admin page (hidden once credentials exist) */}
      {isConnectProvider && credentials.length === 0 && <JiraLinkCodeBlock />}

      {/* Optional footer slot */}
      {footer}
    </div>
  );
};
